import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { managementDb } from '../firebase';
import { toast } from 'react-toastify';
import './JoinUsManagement.css';

const JoinUsManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, reviewed, accepted, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('timestamp'); // timestamp, name, email, department, status
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedApplications, setSelectedApplications] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const prevNewCountRef = useRef(0);
  const [newAlert, setNewAlert] = useState(false);

  useEffect(() => {
    console.log('🔍 Loading applications from Firebase...');
    const applicationsRef = ref(managementDb, 'join-us/applications');
    console.log('🔗 Applications ref created:', applicationsRef);
    
    const unsubscribe = onValue(applicationsRef, (snapshot) => {
      console.log('📥 Applications snapshot received:', snapshot.val());
      const data = snapshot.val();
      if (data) {
        const applicationsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        // Determine the number of 'new' applications and compare with previous
        const newCount = applicationsArray.filter(app => app.status === 'new').length;
        if (prevNewCountRef.current < newCount) {
          const diff = newCount - prevNewCountRef.current;
          console.log(`🔔 ${diff} new application(s) detected`);
          toast.info(`${diff} new application${diff > 1 ? 's' : ''} received`);
          // show temporary alert indicator in UI
          setNewAlert(true);
          setTimeout(() => setNewAlert(false), 7000);
        }
        prevNewCountRef.current = newCount;
        console.log('✅ Applications loaded successfully:', applicationsArray);
        setApplications(applicationsArray);
        // Auto-select first application if none selected
        if (!selectedApplication && applicationsArray.length > 0) {
          setSelectedApplication(applicationsArray[0]);
        }
      } else {
        console.log('📭 No applications found in database');
        setApplications([]);
        setSelectedApplication(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('❌ Error fetching applications:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      toast.error('Failed to load applications');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedApplication]);

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const applicationRef = ref(managementDb, `join-us/applications/${applicationId}`);
      await update(applicationRef, { status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const markAsReviewed = async (applicationId) => {
    try {
      const applicationRef = ref(managementDb, `join-us/applications/${applicationId}`);
      await update(applicationRef, { status: 'reviewed' });
    } catch (error) {
      console.error('Error marking as reviewed:', error);
    }
  };

  const handleApplicationSelect = (application) => {
    setSelectedApplication(application);
    // Automatically mark as reviewed if it's new
    if (application.status === 'new') {
      markAsReviewed(application.id);
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        const applicationRef = ref(managementDb, `join-us/applications/${applicationId}`);
        await remove(applicationRef);
        toast.success('Application deleted successfully');
        if (selectedApplication && selectedApplication.id === applicationId) {
          setSelectedApplication(null);
        }
        setSelectedApplications(prev => {
          const newSet = new Set(prev);
          newSet.delete(applicationId);
          return newSet;
        });
      } catch (error) {
        console.error('Error deleting application:', error);
        toast.error('Failed to delete application');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedApplications.size === 0) {
      toast.warning('Please select applications to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedApplications.size} selected application(s)?`)) {
      try {
        const deletePromises = Array.from(selectedApplications).map(applicationId => {
          const applicationRef = ref(managementDb, `join-us/applications/${applicationId}`);
          return remove(applicationRef);
        });
        
        await Promise.all(deletePromises);
        toast.success(`${selectedApplications.size} application(s) deleted successfully`);
        
        setSelectedApplications(new Set());
        setSelectAll(false);
        
        if (selectedApplication && selectedApplications.has(selectedApplication.id)) {
          setSelectedApplication(null);
        }
      } catch (error) {
        console.error('Error deleting applications:', error);
        toast.error('Failed to delete some applications');
      }
    }
  };

  const handleApplicationSelection = (applicationId) => {
    setSelectedApplications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedApplications(new Set());
      setSelectAll(false);
    } else {
      const allApplicationIds = filteredAndSortedApplications.map(app => app.id);
      setSelectedApplications(new Set(allApplicationIds));
      setSelectAll(true);
    }
  };

  // Update selectAll when applications change
  useEffect(() => {
    if (filteredAndSortedApplications.length > 0) {
      const allSelected = filteredAndSortedApplications.every(app => selectedApplications.has(app.id));
      setSelectAll(allSelected && selectedApplications.size === filteredAndSortedApplications.length);
    } else {
      setSelectAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplications]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const searchInApplication = (application, searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    const searchableFields = [
      application.firstName || '',
      application.lastName || '',
      application.fullName || '',
      application.email || '',
      application.phone || '',
      application.department || '',
      application.portfolio || '',
      application.coverLetter || '',
      application.status || '',
      formatDate(application.timestamp) || ''
    ];
    
    return searchableFields.some(field => 
      field.toLowerCase().includes(searchLower)
    );
  };

  const filteredAndSortedApplications = applications
    .filter(application => {
      const matchesFilter = filter === 'all' || application.status === filter;
      const matchesSearch = searchTerm === '' || searchInApplication(application, searchTerm);
      return matchesFilter && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = (a.fullName || `${a.firstName} ${a.lastName}`).toLowerCase();
          const nameB = (b.fullName || `${b.firstName} ${b.lastName}`).toLowerCase();
          return nameA.localeCompare(nameB);
        case 'email':
          return (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase());
        case 'department':
          return (a.department || '').localeCompare(b.department || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'timestamp':
        default:
          return (b.timestamp || 0) - (a.timestamp || 0);
      }
    });

  const getDepartmentBadge = (department) => {
    const deptColors = {
      'Pre-Production': '#FF6B6B',
      'Production': '#4ECDC4',
      'Post-Production': '#45B7D1',
      'Cast & Crew': '#FFA07A'
    };
    return (
      <span 
        className="department-badge" 
        style={{ backgroundColor: deptColors[department] || '#95A5A6' }}
      >
        {department}
      </span>
    );
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Stats
  const stats = {
    total: applications.length,
    new: applications.filter(app => app.status === 'new').length,
    reviewed: applications.filter(app => app.status === 'reviewed').length,
    accepted: applications.filter(app => app.status === 'accepted').length,
    rejected: applications.filter(app => app.status === 'rejected').length
  };

  if (loading) {
    return (
      <>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading applications...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-briefcase me-2"></i>
                  Join Us Management
                  {stats.new > 0 && (
                    <span className={`header-new-badge ${newAlert ? 'pulse' : ''}`} title={`${stats.new} new application(s)`}>
                      {stats.new}
                    </span>
                  )}
              </h3>
            </div>
            <div className="card-body">
              {/* Stats */}
              <div className="stats-container">
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#1E5EAB' }}>
                <i className="fas fa-file-alt"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Applications</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#FF6B6B' }}>
                <i className="fas fa-envelope"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.new}</div>
                <div className="stat-label">New</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#4ECDC4' }}>
                <i className="fas fa-eye"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.reviewed}</div>
                <div className="stat-label">Reviewed</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#95E1D3' }}>
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.accepted}</div>
                <div className="stat-label">Accepted</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ backgroundColor: '#F38181' }}>
                <i className="fas fa-times-circle"></i>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.rejected}</div>
                <div className="stat-label">Rejected</div>
              </div>
              </div>
              </div>

              {/* Filters and Search */}
              <div className="filters-container mb-4">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Filter by Status</label>
                <select 
                  className="form-select" 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Applications</option>
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Sort By</label>
                <select 
                  className="form-select" 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="timestamp">Newest First</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="department">Department</option>
                  <option value="status">Status</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              </div>
              </div>

              {/* Applications Table */}
              <div className="table-container">
            <div className="table-header-actions">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <label className="form-check-label">Select All</label>
              </div>
              {selectedApplications.size > 0 && (
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={handleBulkDelete}
                >
                  <i className="fas fa-trash"></i> Delete Selected ({selectedApplications.size})
                </button>
              )}
              <span className="applications-count">
                {filteredAndSortedApplications.length} application(s)
              </span>
            </div>
            
            <div className="table-wrapper">
              <table className="applications-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Portfolio</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedApplications.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="empty-state">
                          <i className="fas fa-inbox"></i>
                          <p>No applications found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedApplications.map((application, index) => (
                      <tr
                        key={application.id}
                        className={selectedApplication?.id === application.id ? 'table-row-selected' : ''}
                        onClick={() => handleApplicationSelect(application)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedApplications.has(application.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleApplicationSelection(application.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td>
                          <div className="table-name-cell">
                            <div className="table-avatar">
                              {getInitials(application.firstName, application.lastName)}
                            </div>
                            <div>
                              <div className="table-name">
                                {application.fullName || `${application.firstName} ${application.lastName}`}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <a href={`mailto:${application.email}`} onClick={(e) => e.stopPropagation()}>
                            {application.email}
                          </a>
                        </td>
                        <td>{application.phone || 'N/A'}</td>
                        <td>{getDepartmentBadge(application.department)}</td>
                        <td>
                          {application.portfolio ? (
                            <a 
                              href={application.portfolio} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View <i className="fas fa-external-link-alt ms-1"></i>
                            </a>
                          ) : 'N/A'}
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm table-status-select"
                            value={application.status || 'new'}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleStatusChange(application.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                        <td>{formatDate(application.timestamp)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="btn btn-sm btn-link text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApplicationSelect(application);
                              }}
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-link text-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteApplication(application.id);
                              }}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            </div>

            {/* Application Details Modal */}
            {selectedApplication && (
            <div className="modal-overlay" onClick={() => setSelectedApplication(null)}>
              <div className="modal-content-details" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header-details">
                  <h5>
                    <i className="fas fa-user"></i> Application Details
                  </h5>
                  <button
                    className="btn-close-modal"
                    onClick={() => setSelectedApplication(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="modal-body-details">
                  <div className="detail-section">
                    <h6><i className="fas fa-user"></i> Name</h6>
                    <p>{selectedApplication.fullName || `${selectedApplication.firstName} ${selectedApplication.lastName}`}</p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-envelope"></i> Email</h6>
                    <p>
                      <a href={`mailto:${selectedApplication.email}`}>
                        {selectedApplication.email}
                      </a>
                    </p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-phone"></i> Phone</h6>
                    <p>{selectedApplication.phone || 'N/A'}</p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-briefcase"></i> Department</h6>
                    <p>{getDepartmentBadge(selectedApplication.department)}</p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-globe"></i> Portfolio</h6>
                    <p>
                      {selectedApplication.portfolio ? (
                        <a href={selectedApplication.portfolio} target="_blank" rel="noopener noreferrer">
                          {selectedApplication.portfolio}
                          <i className="fas fa-external-link-alt ms-2"></i>
                        </a>
                      ) : 'N/A'}
                    </p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-file-alt"></i> Cover Letter / Experience</h6>
                    <p className="cover-letter-text">
                      {selectedApplication.coverLetter || 'No cover letter provided.'}
                    </p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-calendar"></i> Submitted</h6>
                    <p>{formatDate(selectedApplication.timestamp)}</p>
                  </div>
                  <div className="detail-section">
                    <h6><i className="fas fa-tag"></i> Status</h6>
                    <select
                      className="form-select"
                      value={selectedApplication.status || 'new'}
                      onChange={(e) => handleStatusChange(selectedApplication.id, e.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JoinUsManagement;